import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  FileText, MessageSquare, Upload, Calendar, LogOut, 
  Send, Clock, CheckCircle, AlertCircle, Loader2,
  Eye, Download, User, Lock, Scale
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Estado del cliente
const useClienteAuth = () => {
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const data = localStorage.getItem('lexduo_cliente');
    if (data) {
      setCliente(JSON.parse(data));
    }
    setLoading(false);
  }, []);

  const login = (clienteData) => {
    localStorage.setItem('lexduo_cliente', JSON.stringify(clienteData));
    setCliente(clienteData);
  };

  const logout = () => {
    localStorage.removeItem('lexduo_cliente');
    setCliente(null);
  };

  return { cliente, loading, login, logout };
};

// Componente de Login
const LoginPortal = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/portal-cliente/login`, {
        email,
        codigo_acceso: codigo
      });

      if (response.data.success) {
        onLogin(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <Scale className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Portal del Cliente</CardTitle>
          <CardDescription>LEXDUO ADVOCATS</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="su@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="codigo">Código de acceso</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="codigo"
                  type="text"
                  placeholder="Código proporcionado"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Accediendo...</>
              ) : (
                'Acceder'
              )}
            </Button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-6">
            Si no dispone de código de acceso, contacte con su abogado.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard del Cliente
const DashboardCliente = ({ cliente, onLogout }) => {
  const { toast } = useToast();
  const [expedientes, setExpedientes] = useState([]);
  const [expedienteActivo, setExpedienteActivo] = useState(null);
  const [detalleExpediente, setDetalleExpediente] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [tabActiva, setTabActiva] = useState('estado');

  useEffect(() => {
    cargarExpedientes();
  }, [cliente.cliente.email]);

  useEffect(() => {
    if (expedienteActivo) {
      cargarDetalleExpediente(expedienteActivo);
      cargarMensajes(expedienteActivo);
    }
  }, [expedienteActivo]);

  const cargarExpedientes = async () => {
    try {
      const response = await axios.get(`${API}/portal-cliente/mis-expedientes?cliente_email=${cliente.cliente.email}`);
      setExpedientes(response.data.expedientes || []);
      if (response.data.expedientes?.length > 0) {
        setExpedienteActivo(response.data.expedientes[0].id);
      }
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudieron cargar los expedientes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const cargarDetalleExpediente = async (expId) => {
    try {
      const response = await axios.get(`${API}/portal-cliente/expediente/${expId}?cliente_email=${cliente.cliente.email}`);
      setDetalleExpediente(response.data);
    } catch (err) {
      console.error('Error cargando detalle:', err);
    }
  };

  const cargarMensajes = async (expId) => {
    try {
      const response = await axios.get(`${API}/portal-cliente/mensajes/${expId}?cliente_email=${cliente.cliente.email}`);
      setMensajes(response.data.mensajes || []);
    } catch (err) {
      console.error('Error cargando mensajes:', err);
    }
  };

  const enviarMensaje = async () => {
    if (!nuevoMensaje.trim()) return;
    setEnviando(true);

    try {
      await axios.post(`${API}/portal-cliente/mensajes?cliente_email=${cliente.cliente.email}`, {
        expediente_id: expedienteActivo,
        contenido: nuevoMensaje
      });
      setNuevoMensaje('');
      cargarMensajes(expedienteActivo);
      toast({ title: 'Mensaje enviado', description: 'Su mensaje ha sido enviado correctamente' });
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo enviar el mensaje', variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('archivo', file);

    try {
      await axios.post(
        `${API}/portal-cliente/documentos/subir?expediente_id=${expedienteActivo}&cliente_email=${cliente.cliente.email}`,
        formData
      );
      toast({ title: 'Documento subido', description: 'El documento se ha subido correctamente' });
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo subir el documento', variant: 'destructive' });
    }
  };

  const getEstadoBadge = (estado) => {
    const estilos = {
      activo: 'bg-green-100 text-green-700',
      suspendido: 'bg-yellow-100 text-yellow-700',
      cerrado: 'bg-gray-100 text-gray-700'
    };
    return estilos[estado] || estilos.activo;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <Scale className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900">Portal del Cliente</h1>
              <p className="text-sm text-gray-500">{cliente.cliente.nombre}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={onLogout}>
            <LogOut className="h-4 w-4 mr-2" /> Salir
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar - Lista de expedientes */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Mis Expedientes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {expedientes.map((exp) => (
                  <button
                    key={exp.id}
                    onClick={() => setExpedienteActivo(exp.id)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      expedienteActivo === exp.id
                        ? 'bg-blue-50 border-blue-200 border'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <p className="font-medium text-sm truncate">{exp.numero_expediente}</p>
                    <p className="text-xs text-gray-500 truncate">{exp.titulo}</p>
                    <Badge className={`mt-1 text-xs ${getEstadoBadge(exp.estado)}`}>
                      {exp.estado}
                    </Badge>
                  </button>
                ))}

                {expedientes.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No tiene expedientes asignados
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Contenido principal */}
          <div className="lg:col-span-3">
            {detalleExpediente ? (
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{detalleExpediente.expediente.numero}</CardTitle>
                      <CardDescription>{detalleExpediente.expediente.titulo}</CardDescription>
                    </div>
                    <Badge className={getEstadoBadge(detalleExpediente.expediente.estado)}>
                      {detalleExpediente.expediente.estado}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <Tabs value={tabActiva} onValueChange={setTabActiva}>
                    <TabsList className="grid grid-cols-3 w-full max-w-md">
                      <TabsTrigger value="estado" className="flex items-center gap-1">
                        <FileText className="h-4 w-4" /> Estado
                      </TabsTrigger>
                      <TabsTrigger value="mensajes" className="flex items-center gap-1">
                        <MessageSquare className="h-4 w-4" /> Mensajes
                        {detalleExpediente.mensajes_no_leidos > 0 && (
                          <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5">
                            {detalleExpediente.mensajes_no_leidos}
                          </span>
                        )}
                      </TabsTrigger>
                      <TabsTrigger value="documentos" className="flex items-center gap-1">
                        <Upload className="h-4 w-4" /> Documentos
                      </TabsTrigger>
                    </TabsList>

                    {/* Tab Estado */}
                    <TabsContent value="estado" className="mt-4">
                      <div className="grid md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-500">Tipo de procedimiento</p>
                          <p className="font-medium">{detalleExpediente.expediente.tipo || 'No especificado'}</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-500">Juzgado</p>
                          <p className="font-medium">{detalleExpediente.expediente.juzgado || 'No especificado'}</p>
                        </div>
                      </div>

                      <h3 className="font-medium mb-3 flex items-center gap-2">
                        <Clock className="h-4 w-4" /> Actuaciones
                      </h3>
                      <div className="space-y-3">
                        {detalleExpediente.actuaciones?.length > 0 ? (
                          detalleExpediente.actuaciones.map((act, idx) => (
                            <div key={idx} className="border-l-2 border-blue-200 pl-4 py-2">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-blue-600" />
                                <span className="font-medium text-sm">{act.titulo || act.tipo}</span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(act.fecha).toLocaleDateString('es-ES', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </p>
                              {act.descripcion && (
                                <p className="text-sm text-gray-600 mt-1">{act.descripcion}</p>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 py-4 text-center">
                            No hay actuaciones visibles
                          </p>
                        )}
                      </div>

                      {detalleExpediente.proximos_eventos?.length > 0 && (
                        <div className="mt-6">
                          <h3 className="font-medium mb-3 flex items-center gap-2">
                            <Calendar className="h-4 w-4" /> Próximos eventos
                          </h3>
                          {detalleExpediente.proximos_eventos.map((ev, idx) => (
                            <div key={idx} className="bg-yellow-50 p-3 rounded-lg mb-2">
                              <p className="font-medium text-sm">{ev.titulo}</p>
                              <p className="text-xs text-gray-600">{ev.fecha}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>

                    {/* Tab Mensajes */}
                    <TabsContent value="mensajes" className="mt-4">
                      <div className="h-80 overflow-y-auto border rounded-lg p-4 mb-4 bg-gray-50">
                        {mensajes.length > 0 ? (
                          <div className="space-y-3">
                            {[...mensajes].reverse().map((msg) => (
                              <div
                                key={msg.id}
                                className={`p-3 rounded-lg max-w-[80%] ${
                                  msg.tipo_remitente === 'cliente'
                                    ? 'bg-blue-100 ml-auto'
                                    : 'bg-white border'
                                }`}
                              >
                                <p className="text-sm">{msg.contenido}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(msg.fecha).toLocaleString('es-ES')}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-gray-500 text-sm py-8">
                            No hay mensajes. Inicie una conversación.
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Textarea
                          placeholder="Escriba su mensaje..."
                          value={nuevoMensaje}
                          onChange={(e) => setNuevoMensaje(e.target.value)}
                          className="flex-1"
                          rows={2}
                        />
                        <Button onClick={enviarMensaje} disabled={enviando || !nuevoMensaje.trim()}>
                          {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                      </div>
                    </TabsContent>

                    {/* Tab Documentos */}
                    <TabsContent value="documentos" className="mt-4">
                      <div className="border-2 border-dashed rounded-lg p-6 text-center mb-4">
                        <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600 mb-2">Subir documento</p>
                        <input
                          type="file"
                          id="file-upload"
                          className="hidden"
                          onChange={handleFileUpload}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                        />
                        <Button variant="outline" size="sm" asChild>
                          <label htmlFor="file-upload" className="cursor-pointer">
                            Seleccionar archivo
                          </label>
                        </Button>
                        <p className="text-xs text-gray-500 mt-2">
                          Formatos: PDF, DOC, DOCX, JPG, PNG, TXT (máx. 10MB)
                        </p>
                      </div>

                      <p className="text-sm text-gray-500 text-center">
                        Los documentos subidos serán revisados por su abogado.
                      </p>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Seleccione un expediente para ver los detalles</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white mt-8">
        <div className="max-w-7xl mx-auto px-4 py-4 text-center text-sm text-gray-500">
          2025 © LEXDUO ADVOCATS SLP - Todos los derechos reservados
        </div>
      </footer>
    </div>
  );
};

// Componente Principal
export default function PortalCliente() {
  const { cliente, loading, login, logout } = useClienteAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!cliente) {
    return <LoginPortal onLogin={login} />;
  }

  return <DashboardCliente cliente={cliente} onLogout={logout} />;
}
